import React, { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, MessageCircle, Send, X, Bot, Sparkles } from 'lucide-react';

const FinanceBotWidget = ({ businessName, onSendMessage }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm FinanceBot, your AI financial assistant. I can help you understand your business finances, answer tax questions, and provide budgeting tips. How can I help you today?" }
  ]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const response = await onSendMessage(userMsg);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (err) {
      console.error("FinanceBot error:", err);
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm having trouble connecting right now. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {open && (
        <Card className="w-96 h-[500px] mb-4 shadow-2xl border-[#E6E8E6] flex flex-col animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-[#0A2B1D] text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-sm flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">FinanceBot</h3>
                <p className="text-xs text-white/80">AI Financial Assistant</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={() => setOpen(false)}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div key={msg.timestamp || `chat-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user' ? 'bg-indigo-600 text-white rounded-br-md' : 'bg-white text-gray-800 shadow-sm border rounded-bl-md'
                }`}>
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-1 mb-1">
                      <Sparkles className="w-3 h-3 text-indigo-500" />
                      <span className="text-xs text-[#0A2B1D] font-medium">FinanceBot</span>
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm border rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                    <span className="text-sm text-gray-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t bg-white rounded-b-lg">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your finances..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && send()}
                disabled={loading}
              />
              <Button size="icon" className="bg-[#0A2B1D] hover:bg-[#254D3D] rounded-sm" onClick={send} disabled={loading || !input.trim()}>
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {businessName ? `Analyzing ${businessName}` : 'Select a business for personalized insights'}
            </p>
          </div>
        </Card>
      )}

      <Button
        onClick={() => setOpen(!open)}
        className={`w-14 h-14 rounded-sm shadow-lg transition-all duration-300 ${
          open ? 'bg-[#5C6661] hover:bg-[#111614]' : 'bg-[#0A2B1D] hover:bg-[#254D3D]'
        }`}
        data-testid="finance-bot-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </Button>
    </div>
  );
};

export default FinanceBotWidget;
